import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Cpu, Pencil, Users } from "lucide-react";

import PageLayout from "@/components/PageLayout";
import ProjectChat from "@/components/ProjectChat";
import StatusBadge from "@/components/StatusBadge";
import WizardStepper, { WizardStep } from "@/components/WizardStepper";
import ProjectAccessDenied from "@/components/ProjectAccessDenied";
import ProjectCustomFields from "@/components/ProjectCustomFields";
import DocumentUploadForm from "@/components/DocumentUploadForm";
import * as DocumentToolbar from "@/components/DocumentToolbar";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import { PROJECT_MODEL_OPTIONS } from "@/components/ProjectForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { projectTypeLabel, projectTypeVariant } from "@/config/projectTypeConfig";
import { PROJECT_STATUS } from "@/config/projectStatus";
import {
  useProject,
  useProjectDocuments,
  useUpdateProject,
} from "@/hooks/useProjects";
import { useProjectAccess } from "@/hooks/useProjectAccess";
import { useProjectCustomFields } from "@/hooks/useProjectCustomFields";
import { useDocuments } from "@/hooks/useDocumentMetadata";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { useIsProjectStreaming } from "@/services/chatStreamStore";
import { toast } from "sonner";
import { EvidenceBase } from "@lumanity/ui/evidence";
import { useOpenEvidenceDocument, useRefreshDocuments } from "@/lib/evidence";

const STEPS: WizardStep[] = [
  { id: "fields", label: "Custom Fields" },
  { id: "upload", label: "Upload Files" },
  { id: "evidence", label: "Evidence Base" },
  { id: "chat", label: "Ready to Chat" },
];

const modelLabel = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  return PROJECT_MODEL_OPTIONS.find((opt) => opt.value === value)?.label ?? value;
};

const stepFromStatus = (
  status: string | undefined,
  hasCustomFields: boolean,
  hasDocuments: boolean,
): number => {
  if (status === PROJECT_STATUS.Complete || status === PROJECT_STATUS.InProgress) return 3;
  if (status === PROJECT_STATUS.EvidenceBase || hasDocuments) return 2;
  if (status === PROJECT_STATUS.DocumentUpload && hasCustomFields) return 1;
  return 0;
};

const ProjectDetail = () => {
  const { id = "" } = useParams<{ id: string }>();
  const { data: project, isLoading: projectLoading, isError: projectError } = useProject(id);
  const { data: customFields = [], isLoading: customFieldsLoading } = useProjectCustomFields(id);
  const {
    data: projectDocuments = [],
    isLoading: projectDocsLoading,
    refetch: refetchProjectDocuments,
  } = useProjectDocuments(id);
  const { data: allDocuments = [] } = useDocuments();
  const { hasAccess, isLoading: accessLoading } = useProjectAccess(project);
  const updateProject = useUpdateProject();
  const openDocument = useOpenEvidenceDocument();
  const refreshDocuments = useRefreshDocuments();

  const activeDocIds = useMemo(
    () => new Set(projectDocuments.filter((p) => p.isActive).map((p) => p.documentId)),
    [projectDocuments],
  );
  const documents = useMemo(
    () => allDocuments.filter((d) => activeDocIds.has(d.id)),
    [allDocuments, activeDocIds],
  );

  const hasDocuments = documents.length > 0;

  const [activeStep, setActiveStep] = useState(0);
  const [stepInitialized, setStepInitialized] = useState(false);
  const [uploadDirty, setUploadDirty] = useState(false);
  // Chat streaming is deliberately NOT part of this guard: a streamed reply
  // now survives leaving the page (see chatStreamStore), so blocking
  // navigation for it would trap the user for no gain.
  const { isBlocked, confirmLeave, cancelLeave, guardedNavigate } = useUnsavedChangesGuard(uploadDirty);

  // Flag a background response on the chat step so a user who stepped away
  // can see one is still coming.
  const chatStreaming = useIsProjectStreaming(id ?? "");
  const steps = useMemo(
    () =>
      STEPS.map((step) =>
        step.id === "chat" && chatStreaming ? { ...step, hint: "generating…" } : step,
      ),
    [chatStreaming],
  );

  useEffect(() => {
    if (stepInitialized || !project) return;
    // Wait for the queries that determine the starting step — otherwise a
    // freshly mounted page could init at step 0 before fields/docs arrive.
    if (customFieldsLoading || projectDocsLoading) return;
    setActiveStep(stepFromStatus(project.status, customFields.length > 0, hasDocuments));
    setStepInitialized(true);
  }, [
    project,
    customFields.length,
    customFieldsLoading,
    hasDocuments,
    projectDocsLoading,
    stepInitialized,
  ]);

  const completedIndices = useMemo(() => {
    const completed: number[] = [];
    if (customFields.length > 0) completed.push(0);
    const pastUpload =
      project?.status === PROJECT_STATUS.EvidenceBase ||
      project?.status === PROJECT_STATUS.InProgress ||
      project?.status === PROJECT_STATUS.Complete;
    if (hasDocuments || pastUpload) {
      completed.push(1);
    }
    if (project?.status === PROJECT_STATUS.InProgress || project?.status === PROJECT_STATUS.Complete) {
      completed.push(2, 3);
    }
    return completed;
  }, [customFields.length, hasDocuments, project?.status]);

  const handleUploadComplete = async () => {
    await refetchProjectDocuments();
    if (project?.status === PROJECT_STATUS.DocumentUpload && id) {
      try {
        await updateProject.mutateAsync({ id, status: PROJECT_STATUS.EvidenceBase });
      } catch {
        /* non-blocking */
      }
    }
    setActiveStep(2);
  };


  const handleAdvanceToChat = async () => {
    if (id && project?.status === PROJECT_STATUS.EvidenceBase) {
      try {
        await updateProject.mutateAsync({ id, status: PROJECT_STATUS.InProgress });
      } catch {
        /* non-blocking */
      }
    }
    setActiveStep(3);
  };

  if (projectLoading || accessLoading) {
    return (
      <PageLayout>
        <div className="text-muted-foreground py-12 text-center">Loading project…</div>
      </PageLayout>
    );
  }

  if (projectError || !project) {
    return (
      <PageLayout>
        <div className="py-12 text-center space-y-4">
          <p className="text-muted-foreground">Project not found.</p>
          <Button asChild variant="outline">
            <Link to="/projects">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Projects
            </Link>
          </Button>
        </div>
      </PageLayout>
    );
  }

  if (!hasAccess) {
    return <ProjectAccessDenied projectId={project.id} />;
  }

  const isEvidenceStep = activeStep === 2;
  const isChatStep = activeStep === 3;

  return (
    <PageLayout fullHeight={isEvidenceStep || isChatStep}>
      <div className="mb-4">
        <Button asChild variant="ghost" size="sm" className="gap-1 -ml-2">
          <Link to="/projects">
            <ArrowLeft className="w-4 h-4" /> Back to Projects
          </Link>
        </Button>
      </div>

      <div className="bg-card border rounded-lg p-6 mb-2">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h1 className="text-2xl font-bold text-brand-navy">{project.name}</h1>
              {project.projectType && (
                <Badge variant={projectTypeVariant[project.projectType] ?? "secondary"}>
                  {projectTypeLabel[project.projectType] ?? project.projectType}
                </Badge>
              )}
              <StatusBadge status={project.status} />
            </div>
            {project.objective && (
              <p className="text-sm text-muted-foreground mb-3">{project.objective}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{project.code}</span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Started {project.startDate}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" /> {project.teamMembers} team members
              </span>
              {modelLabel(project.model) && (
                <span className="flex items-center gap-1">
                  <Cpu className="w-3 h-3" /> {modelLabel(project.model)}
                </span>
              )}
            </div>
          </div>
          <Button asChild variant="outline" size="sm" className="gap-1">
            <Link to={`/projects/${project.id}/edit`}>
              <Pencil className="w-4 h-4" /> Edit
            </Link>
          </Button>
        </div>
      </div>

      <WizardStepper
        steps={steps}
        activeIndex={activeStep}
        completedIndices={completedIndices}
        onStepClick={(idx) => guardedNavigate(() => setActiveStep(idx))}
      />

      <UnsavedChangesDialog open={isBlocked} onConfirm={confirmLeave} onCancel={cancelLeave} />

      {activeStep === 0 && (
        <div>
          <ProjectCustomFields projectId={project.id} />
          <div className="flex justify-end">
            <Button
              className="bg-brand-teal text-white hover:bg-brand-teal/90 gap-1"
              onClick={() => setActiveStep(1)}
            >
              Continue <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {activeStep === 1 && (
        <div className="bg-card border rounded-lg p-6">
          <DocumentUploadForm
            projectId={id}
            projectModel={project.model}
            isAdvancedRag={project.isAdvancedRag}
            customFields={customFields}
            onComplete={handleUploadComplete}
            lockedOwnership="Private"
            onDirtyChange={setUploadDirty}
          />
        </div>
      )}

      {activeStep === 2 && (
        <EvidenceBase
          fillHeight
          projectId={id}
          projectModel={project.model}
          isAdvancedRag={project.isAdvancedRag}
          onOpenDocument={openDocument}
          onSaved={() => refreshDocuments(id)}
          onDeleted={() => refreshDocuments(id)}
          onError={(e) => toast.error(e.message)}
          toolbarActions={
            <>
              <DocumentToolbar.UploadButton onClick={() => setActiveStep(1)} />
              <Button
                size="sm"
                className="bg-brand-teal text-white hover:bg-brand-teal/90 gap-1"
                onClick={handleAdvanceToChat}
                disabled={!hasDocuments}
              >
                Continue <ArrowRight className="w-4 h-4" />
              </Button>
            </>
          }
        />
      )}

      {activeStep === 3 && <ProjectChat projectId={id} isAdvancedRag={project.isAdvancedRag} />}
    </PageLayout>
  );
};

export default ProjectDetail;
